<template>
    <div>
        <div v-if="loading && !health" aria-busy="true" aria-label="Loading vehicle health">
            <v-skeleton-loader class="mb-4" type="list-item-avatar-two-line"/>
            <v-row dense>
                <v-col cols="12" md="4"><v-skeleton-loader type="card"/></v-col>
                <v-col cols="12" md="8"><v-skeleton-loader type="card"/></v-col>
            </v-row>
        </div>

        <v-alert v-else-if="noAccess" density="compact" type="info" variant="tonal">
            Your role does not include vehicle health yet. Ask a company admin for access.
        </v-alert>

        <v-alert v-else-if="loadError && !health" density="compact" type="error" variant="tonal">
            Vehicle health could not be loaded.
            <template #append>
                <v-btn :loading="loading" size="small" variant="text" @click="refresh">Retry</v-btn>
            </template>
        </v-alert>

        <template v-else>
            <v-skeleton-loader v-if="codeStatus === 'loading'" class="mb-4" type="list-item-avatar-two-line"/>
            <v-alert v-else-if="verdict" :color="verdict.color" :icon="verdict.icon" class="mb-4" prominent
                     role="status" variant="tonal">
                <div class="text-h6 font-weight-bold">{{ verdict.title }}</div>
                <div v-if="verdict.detail" class="text-body-2">{{ verdict.detail }}</div>
                <div class="text-caption text-medium-emphasis mt-1">
                    As of {{ formatCompanyDateTime(paramsAsOf) }}
                </div>
            </v-alert>

            <v-alert v-if="!paramsAsOf" class="mb-4" density="compact" icon="mdi-information-outline" variant="tonal">
                No engine data reported yet
            </v-alert>

            <v-row dense>
                <v-col cols="12" md="4">
                    <v-card class="h-100" variant="outlined">
                        <v-card-text>
                            <div class="d-flex align-center justify-space-between">
                                <span class="text-subtitle-2 text-medium-emphasis">Engine Hours</span>
                                <v-chip v-if="sourceBadge" :color="sourceBadge.color" size="x-small" variant="tonal">
                                    {{ sourceBadge.label }}
                                </v-chip>
                            </div>
                            <div class="text-h5 font-weight-bold my-1">{{ formatHours(engineHours?.value) }}</div>
                            <div v-if="periodHours" class="text-body-2">
                                +{{ formatNumber(periodHours.added) }} h this period,
                                {{ formatNumber(periodHours.perDay) }} h per day over the period
                            </div>
                            <div v-if="engineHours?.as_of" class="text-caption text-medium-emphasis">
                                As of {{ formatCompanyDateTime(engineHours.as_of) }}
                            </div>
                            <div v-if="engineHours?.raw != null && Number(engineHours?.offset)" class="text-caption text-medium-emphasis">
                                Includes a {{ formatHours(engineHours.offset) }} adjustment
                            </div>
                            <v-alert v-if="engineHours?.stale" class="mt-2" density="compact" type="warning" variant="tonal">
                                This reading came from a device that is no longer on this vehicle.
                            </v-alert>
                            <v-btn v-if="canSetHours" class="mt-3" color="primary" prepend-icon="mdi-pencil"
                                   size="small" variant="tonal" @click="hoursDialog = true">
                                Set hours
                            </v-btn>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="8">
                    <v-row dense>
                        <v-col v-for="tile in shownTiles" :key="tile.key" cols="6" sm="4">
                            <v-card :style="tile.status ? `border-color: rgb(var(--v-theme-${tile.status.color}))` : undefined"
                                    class="h-100" variant="outlined">
                                <v-card-text class="pa-3">
                                    <div class="text-caption text-medium-emphasis">
                                        <v-icon :icon="tile.icon" class="me-1" size="small"/>{{ tile.label }}
                                    </div>
                                    <div class="d-flex flex-wrap align-center text-subtitle-1 font-weight-bold">
                                        {{ tile.display }}
                                        <v-chip v-if="tile.status" :color="tile.status.color" class="ms-2" size="x-small" variant="flat">
                                            {{ tile.status.label }}
                                        </v-chip>
                                    </div>
                                    <div v-if="tile.sub" class="text-caption text-medium-emphasis">{{ tile.sub }}</div>
                                    <div v-if="tile.trend.length > 1" :aria-label="tile.trendLabel" class="my-1" role="img">
                                        <v-sparkline :color="tile.status?.color ?? 'primary'" :model-value="tile.trend"
                                                     height="40" line-width="2" padding="4" smooth/>
                                    </div>
                                    <div v-if="tile.asOf" class="text-caption text-medium-emphasis">
                                        As of {{ formatCompanyDateTime(tile.asOf) }}
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <div v-if="hasData && notReported.length" class="text-caption text-medium-emphasis mt-2">
                        Not reported by this vehicle: {{ notReported.join(', ') }}
                    </div>
                </v-col>
            </v-row>

            <J1939LampChips :lamps="lamps"/>
            <div v-if="lampsAt && !lampsFresh" class="text-caption text-medium-emphasis mt-1">
                Warning lamps last reported {{ formatCompanyDateTime(lampsAt, DATE_ONLY) }}, more than {{ FRESH_DAYS }} days ago
            </div>

            <v-card v-if="codeStatus !== 'denied'" class="mt-3" variant="outlined">
                <v-card-title class="d-flex flex-wrap align-center text-subtitle-1">
                    Fault Codes
                    <v-spacer/>
                    <v-btn v-if="dtcTabAvailable" append-icon="mdi-arrow-right" color="primary" size="small"
                           variant="text" @click="$emit('show-dtcs')">
                        View diagnostic codes
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <v-skeleton-loader v-if="codeStatus === 'loading'" type="list-item-two-line@2"/>
                    <div v-else-if="codeStatus === 'error'" class="text-medium-emphasis">
                        Fault codes could not be loaded.
                    </div>
                    <div v-else-if="!faultCodes.length" class="d-flex flex-wrap align-center ga-2">
                        <v-chip v-if="codeStatus !== 'ok' || !paramsAsOf" size="small" variant="tonal">
                            No fault codes reported yet
                        </v-chip>
                        <v-chip v-else :color="codesFresh ? 'success' : undefined"
                                :prepend-icon="codesFresh ? 'mdi-check-circle' : undefined" size="small" variant="tonal">
                            No fault codes
                        </v-chip>
                        <span v-if="codeStatus === 'ok' && dtcs?.event_datetime" class="text-caption text-medium-emphasis">
                            As of {{ formatCompanyDateTime(dtcs.event_datetime) }}
                        </span>
                    </div>
                    <template v-else>
                        <v-list class="py-0" density="compact">
                            <v-list-item v-for="dtc in faultCodes" :key="dtc.code" class="px-0">
                                <div class="d-flex flex-wrap align-center ga-2">
                                    <v-chip :color="codesFresh ? dtc.color : undefined" class="font-weight-bold" label size="small">
                                        {{ dtc.code }}
                                    </v-chip>
                                    <span class="text-caption text-medium-emphasis">{{ dtc.typeLabel }}</span>
                                </div>
                                <div class="text-body-2 mt-1">{{ dtc.user_description || dtc.description }}</div>
                                <div v-if="dtc.suggested_fix" class="text-body-2 text-success mt-1">
                                    <v-icon class="me-1" icon="mdi-wrench" size="x-small"/>{{ dtc.suggested_fix }}
                                </div>
                            </v-list-item>
                        </v-list>
                        <div v-if="dtcs?.event_datetime" class="text-caption text-medium-emphasis mt-2">
                            Reported {{ formatCompanyDateTime(dtcs.event_datetime) }}<template v-if="!codesFresh">,
                            more than {{ FRESH_DAYS }} days ago</template>
                        </div>
                    </template>
                </v-card-text>
            </v-card>

            <v-divider class="my-4"/>

            <div class="d-flex flex-wrap align-center ga-3">
                <span id="vh-range-label" class="text-subtitle-2 text-medium-emphasis">History</span>
                <v-btn-toggle v-model="rangePreset" aria-labelledby="vh-range-label" color="primary" density="compact"
                              divided mandatory variant="outlined" @update:model-value="onPresetChange">
                    <v-btn v-for="preset in PRESETS" :key="preset.value" :aria-pressed="String(rangePreset === preset.value)"
                           :value="preset.value" size="small">
                        {{ preset.label }}
                    </v-btn>
                </v-btn-toggle>
                <v-progress-circular v-if="historyLoading" aria-label="Loading history" color="primary" indeterminate
                                     size="18" width="2"/>
            </div>
            <v-row v-if="rangePreset === 'custom'" class="mt-1" dense>
                <v-col cols="12" sm="4" md="3">
                    <v-text-field v-model="fromDate" :max="toDate" density="compact" hide-details label="From" type="date"/>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                    <v-text-field v-model="toDate" :min="fromDate" density="compact" hide-details label="To" type="date"/>
                </v-col>
            </v-row>
            <div v-if="rangeError" class="text-error text-caption mt-1">{{ rangeError }}</div>
            <div v-else-if="historyTruncated" class="text-warning text-caption mt-1">
                Showing the first {{ MAX_HISTORY_ROWS.toLocaleString() }} readings; pick a shorter range.
            </div>

            <v-row v-if="!rangeError" class="mt-2">
                <v-col v-for="chart in charts" :key="chart.key" cols="12" md="6">
                    <v-card variant="outlined">
                        <v-card-title class="text-subtitle-1">{{ chart.title }}</v-card-title>
                        <v-card-text>
                            <v-skeleton-loader v-if="historyLoading && !chart.hasData" height="260" type="image"/>
                            <template v-else-if="chart.hasData">
                                <div :aria-label="chart.summary" class="chart-box" role="img">
                                    <Line :data="chart.data" :options="chart.options"/>
                                </div>
                                <div v-if="chart.note" class="text-caption text-medium-emphasis mt-1">{{ chart.note }}</div>
                            </template>
                            <div v-else class="text-medium-emphasis text-caption py-6 text-center">
                                No readings in this range
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <EngineHoursDialog v-if="canSetHours" v-model="hoursDialog" :asset-id="assetId"
                           :current-hours="engineHours?.value" @saved="refresh"/>
    </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {Line} from "vue-chartjs";
import {Chart, registerables} from "chart.js";
import {send} from "@/composables/apiClient";
import {isCompanyAdminRole, strOperations} from "@/composables/globals";
import {
    companyDateTimeFormatter, companyTimezone, formatCompanyDateTime, toZonedInput, zonedInputToUtcIso,
} from "@/composables/useTimezone";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import J1939LampChips from "@/components/cards/assets/J1939LampChips.vue";
import EngineHoursDialog from "@/pages/Asset/Details/EngineHoursDialog.vue";

Chart.register(...registerables);

const props = defineProps({
    assetId: {type: [String, Number], required: true},
    assetDetails: {type: Object, default: null},
    dtcTabAvailable: {type: Boolean, default: false},
    // The assets/{id}/dtcs payload Details.vue loads, and its state: loading, ok, none, denied or error
    dtcs: {type: [Object, Array], default: null},
    dtcStatus: {type: String, default: ''},
});
const emit = defineEmits(["show-dtcs", "no-access"]);

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const {unitifyVolume, unitifyTemperature, unitifyPressure, unitLabels} = strOperations();

const MAX_RANGE_DAYS = 90;
const MAX_HISTORY_ROWS = 20000;
const DAY_MS = 86400000;
const HOUR_MS = 3600000;
const SPARKLINE_POINTS = 60;
// Readings older than this cannot back a verdict
const FRESH_DAYS = 7;
const DATE_ONLY = {hour: undefined, minute: undefined, second: undefined};
// One set of 12 V limits for the tile, the banner and the chart band; a 24 V system doubles them
const BATTERY = {veryLow: 11.6, low: 12.0, high: 15.3, veryHigh: 15.8, bandLow: 12.0, bandHigh: 15.0};
const BATTERY_24V_ABOVE = 17;
const DEF_LOW_PCT = 15;
const DEF_VERY_LOW_PCT = 5;
const COOLANT_HOT_C = 105;
const PRESETS = [
    {value: '7', label: '7 days'},
    {value: '30', label: '30 days'},
    {value: '90', label: '90 days'},
    {value: 'custom', label: 'Custom'},
];

const health = ref(null);
const loading = ref(false);
const history = ref([]);
const historyLoading = ref(false);
const historyTruncated = ref(false);
const historyDays = ref(null);
const loadError = ref(false);
let historyRequest = 0;
const hoursDialog = ref(false);
const rangeError = ref('');
// The API answers 400 when the role lacks the permission, so show a note instead of error toasts
const noAccess = ref(false);
const isNoAccess = (error) => error.response?.status === 400 && /not permitted/i.test(error.response?.data?.message ?? '');

const tz = companyTimezone();
const todayInput = () => toZonedInput(new Date().toISOString(), tz).slice(0, 10);

// A YYYY-MM-DD input as a UTC day number, so ranges count calendar days whatever the DST changes
const dayNumber = (value) => {
    const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY_MS : null;
};
const dayInput = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const isFresh = (at) => !!at && Date.now() - new Date(at).getTime() <= FRESH_DAYS * DAY_MS;

const rangePreset = ref('30');
const toDate = ref(todayInput());
const fromDate = ref(dayInput(dayNumber(toDate.value) - 29));

// Same rule as the API: company admins, plus platform admins helping a customer
const canSetHours = computed(() => authStore.user?.type === 'admin' || isCompanyAdminRole(authStore.user));
const engineHours = computed(() => health.value?.engine_hours ?? null);
const paramsAsOf = computed(() => health.value?.latest_params?.event_datetime ?? null);
const engineFresh = computed(() => isFresh(paramsAsOf.value));
const dtcCount = computed(() => health.value?.dtc_count ?? null);
// Fall back to the lamps on the asset details when the health payload has none.
const lamps = computed(() => {
    const l = health.value?.lamps ?? props.assetDetails?.j1939_lamps ?? null;
    return l && l.red_stop_lamp != null ? l : null;
});
// Lamp state counts toward the verdict only when its own report is recent; older API builds send no time
const lampsAt = computed(() => health.value?.check_engine?.at ?? health.value?.lamps?.at ?? null);
const lampsFresh = computed(() => (health.value && 'check_engine' in health.value ? isFresh(lampsAt.value) : true));
const checkEngineOn = computed(() => (health.value?.check_engine
    ? !!health.value.check_engine.on : !!props.assetDetails?.alert_lamb_status));

// A failed code load still counts as no codes when the health card says there are none
const codeStatus = computed(() => (props.dtcStatus === 'error' && dtcCount.value === 0 ? 'none' : props.dtcStatus));
const codesFresh = computed(() => codeStatus.value === 'ok' && isFresh(props.dtcs?.event_datetime));

const paramItem = (key) => (health.value?.latest_params?.params ?? []).find(p => p.key === key);
const param = (key) => {
    const value = paramItem(key)?.value;
    return value === undefined ? null : value;
};
// The banner shows the card time; a tile shows its own only when more than an hour away from it
const paramAsOf = (key) => {
    const at = paramItem(key)?.at;
    if (!at || !paramsAsOf.value) return null;
    return Math.abs(new Date(at) - new Date(paramsAsOf.value)) > HOUR_MS ? at : null;
};

const hasData = computed(() => engineHours.value?.value != null
    || (health.value?.latest_params?.params ?? []).some(p => p.value != null));

const SOURCES = {
    ecu: {label: 'Engine ECU', color: 'success'},
    ecu_other: {label: 'Other module', color: 'success'},
    obd: {label: 'OBD', color: 'success'},
    manual: {label: 'Manual', color: 'info'},
    estimated: {label: 'Estimated', color: 'warning'},
};
// No source with a value means the hours come only from a manual entry
const sourceBadge = computed(() => {
    const hours = engineHours.value;
    if (!hours) return null;
    return SOURCES[hours.source ?? (hours.value != null ? 'manual' : '')] ?? null;
});

const formatNumber = (value) => Number(value).toLocaleString(undefined, {maximumFractionDigits: 1});
const formatHours = (hours) => {
    if (hours === null || hours === undefined || hours === '' || isNaN(Number(hours))) return 'Not reported';
    return `${formatNumber(hours)} h`;
};
const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const columnValues = (column, convert = Number) => history.value
    .map(r => r[column])
    .filter(v => v !== null && v !== undefined)
    .map(convert);

const batteryScale = computed(() => {
    const latest = param('battery_voltage');
    const highest = Math.max(latest != null ? Number(latest) : 0, ...columnValues('battery_v'));
    return highest > BATTERY_24V_ABOVE ? 2 : 1;
});

const batteryStatus = (volts) => {
    if (volts == null) return null;
    const v = Number(volts) / batteryScale.value;
    if (v < BATTERY.veryLow) return {color: 'error', label: 'Very low'};
    if (v > BATTERY.veryHigh) return {color: 'error', label: 'Very high'};
    if (v < BATTERY.low) return {color: 'warning', label: 'Low'};
    if (v > BATTERY.high) return {color: 'warning', label: 'High'};
    return null;
};
const defStatus = (pct) => {
    if (pct == null) return null;
    if (Number(pct) < DEF_VERY_LOW_PCT) return {color: 'error', label: 'Very low'};
    if (Number(pct) < DEF_LOW_PCT) return {color: 'warning', label: 'Low'};
    return null;
};
const coolantStatus = (celsius) => (celsius != null && Number(celsius) >= COOLANT_HOT_C ? {color: 'warning', label: 'Hot'} : null);

// Thin a long series so the sparkline stays light; the last reading is always kept
const thin = (values) => {
    const step = Math.ceil(values.length / SPARKLINE_POINTS) || 1;
    return values.filter((_, i) => i % step === 0 || i === values.length - 1);
};
const trendLabel = (label, values, unit) => {
    if (values.length < 2) return '';
    const fmt = v => `${formatNumber(v)}${unit}`;
    return `${label} trend over the selected range: latest ${fmt(values[values.length - 1])}, low ${fmt(Math.min(...values))}, high ${fmt(Math.max(...values))}`;
};

const tiles = computed(() => {
    const units = unitLabels();
    const engine = param('engine_hours');
    const idle = param('idle_hours');
    const battery = param('battery_voltage');
    const coolant = param('coolant_temp');
    const def = param('def_level');
    const idlePct = idle != null && engine > 0 ? `${((idle / engine) * 100).toFixed(1)}% of engine hours` : null;
    return [
        {key: 'idle', label: 'Idle Hours', missing: 'idle hours', icon: 'mdi-timer-sand', column: 'idle_hours', unit: ' h',
            display: idle != null ? formatHours(idle) : null, sub: idlePct, asOf: paramAsOf('idle_hours')},
        {key: 'fuel', label: 'Total Fuel Used', missing: 'total fuel used', icon: 'mdi-gas-station', column: 'total_fuel_l',
            unit: ` ${units.volume}`, convert: v => unitifyVolume(v, false),
            display: unitifyVolume(param('total_fuel')), asOf: paramAsOf('total_fuel')},
        {key: 'battery', label: 'Battery', missing: 'battery voltage', icon: 'mdi-car-battery', column: 'battery_v', unit: ' V',
            display: battery != null ? `${Number(battery).toFixed(1)} V` : null, status: batteryStatus(battery),
            cause: battery != null ? `battery ${Number(battery).toFixed(1)} V` : null, asOf: paramAsOf('battery_voltage')},
        {key: 'coolant', label: 'Coolant', missing: 'coolant temperature', icon: 'mdi-thermometer', column: 'coolant_c',
            unit: ` ${units.temperature}`, convert: v => unitifyTemperature(v, false),
            display: unitifyTemperature(coolant), status: coolantStatus(coolant),
            cause: `coolant ${unitifyTemperature(coolant)}`, asOf: paramAsOf('coolant_temp')},
        {key: 'oil', label: 'Oil Pressure', missing: 'oil pressure', icon: 'mdi-oil', column: 'oil_kpa',
            unit: ` ${units.pressure}`, convert: v => unitifyPressure(v, false),
            display: unitifyPressure(param('oil_pressure')), asOf: paramAsOf('oil_pressure')},
        {key: 'def', label: 'DEF Level', missing: 'DEF level', icon: 'mdi-water-percent', column: 'def_pct', unit: '%',
            display: def != null ? `${Number(def).toFixed(0)}%` : null, status: defStatus(def),
            cause: def != null ? `DEF ${Number(def).toFixed(0)}%` : null, asOf: paramAsOf('def_level')},
    ];
});
const shownTiles = computed(() => tiles.value
    .filter(tile => tile.display != null)
    .map((tile) => {
        const values = columnValues(tile.column, v => Number(tile.convert ? tile.convert(v) : v));
        return {...tile, trend: thin(values), trendLabel: trendLabel(tile.label, values, tile.unit)};
    }));
const notReported = computed(() => tiles.value.filter(tile => tile.display == null).map(tile => tile.missing));

// J1939 trucks report the DM1 lamps; OBD-II cars only the check engine light (MIL)
const engineLampOn = computed(() => lampsFresh.value && (lamps.value ? lamps.value.red_stop_lamp === 1 : checkEngineOn.value));

// One row per code. Red only for a stored code with the lamp on; a stored code with the lamp off,
// a permanent code (already cleared, kept until the car re-tests) and a pending code are amber.
const faultCodes = computed(() => {
    const groups = codeStatus.value === 'ok' ? (props.dtcs?.description ?? {}) : {};
    const rank = {stored: 3, permanent: 2, pending: 1};
    const byCode = new Map();
    Object.entries(groups).forEach(([type, codes]) => {
        const kind = /pending/i.test(type) ? 'pending' : (/permanent/i.test(type) ? 'permanent' : 'stored');
        (codes ?? []).forEach((dtc) => {
            const seen = byCode.get(dtc.code);
            if (seen && rank[seen.kind] >= rank[kind]) return;
            byCode.set(dtc.code, {...dtc, kind});
        });
    });
    const labels = {stored: 'Confirmed', permanent: 'Permanent', pending: 'Pending'};
    return [...byCode.values()]
        .map(dtc => ({
            ...dtc,
            active: dtc.kind === 'stored' && engineLampOn.value,
            color: dtc.kind === 'stored' && engineLampOn.value ? 'error' : 'warning',
            typeLabel: labels[dtc.kind],
        }))
        .sort((a, b) => rank[b.kind] - rank[a.kind]);
});

const verdict = computed(() => {
    if (!paramsAsOf.value) return null;
    if (!engineFresh.value) {
        return {
            color: 'grey', icon: 'mdi-help-circle-outline',
            title: `Last engine data ${formatCompanyDateTime(paramsAsOf.value, DATE_ONLY)}, status unknown`, detail: '',
        };
    }
    const red = [];
    const amber = [];
    const l = lampsFresh.value ? lamps.value : null;
    if (l) {
        if (l.red_stop_lamp === 1) red.push('red stop lamp on');
        if (l.amber_warning_lamp === 1) amber.push('amber warning lamp on');
        if (l.protect_lamp === 1) amber.push('protect lamp on');
    } else if (lampsFresh.value && checkEngineOn.value) {
        (faultCodes.value.some(d => d.active && codesFresh.value) ? red : amber).push('check engine light on');
    }
    if (codesFresh.value) {
        const active = faultCodes.value.filter(d => d.active).length;
        const other = faultCodes.value.length - active;
        if (active) red.unshift(plural(active, 'active fault code'));
        if (other) amber.push(plural(other, 'fault code'));
    } else if (codeStatus.value !== 'ok' && codeStatus.value !== 'none' && dtcCount.value > 0) {
        // The codes did not load but the vehicle reports some, so never show green
        amber.push(plural(dtcCount.value, 'fault code'));
    }
    tiles.value.filter(tile => tile.status && tile.cause).forEach(tile => amber.push(tile.cause));

    const causes = [...red, ...amber];
    const detail = causes.join(', ').replace(/^./, c => c.toUpperCase());
    if (red.length) return {color: 'error', icon: 'mdi-alert-octagon', title: 'Needs attention now', detail};
    if (amber.length) return {color: 'warning', icon: 'mdi-alert', title: 'Check soon', detail};
    const codesText = codesFresh.value ? 'No fault codes' : 'No recent fault code report';
    return {
        color: 'success', icon: 'mdi-check-circle', title: 'All systems normal',
        detail: `${codesText}, no warning lamps, and every reading is in its normal range.`,
    };
});

// Device hours added over the loaded range, from rows of the same source as the newest one only,
// adding up forward steps so a swap or a reset cannot count as engine time
const periodHours = computed(() => {
    const rows = history.value.filter(r => r.engine_hours !== null && r.engine_hours !== undefined);
    if (rows.length < 2 || !historyDays.value) return null;
    const sourceOf = r => `${Number(r.flags ?? 0) & 12}:${r.hours_sa ?? ''}`;
    const source = sourceOf(rows[rows.length - 1]);
    const same = rows.filter(r => sourceOf(r) === source);
    if (same.length < 2) return null;
    let added = 0;
    for (let i = 1; i < same.length; i++) {
        const step = Number(same[i].engine_hours) - Number(same[i - 1].engine_hours);
        if (step > 0) added += step;
    }
    const spanHours = (new Date(same[same.length - 1].event_datetime) - new Date(same[0].event_datetime)) / HOUR_MS;
    // More engine time than clock time means the readings are not trustworthy
    if (added > spanHours) return null;
    return {added, perDay: added / historyDays.value};
});

const lineOptions = (axes) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {mode: 'index', intersect: false},
    plugins: {
        legend: {display: true, position: 'top', labels: {filter: (item, data) => !data.datasets[item.datasetIndex]?.band}},
        tooltip: {filter: item => !item.dataset.band},
    },
    elements: {point: {radius: 0}},
    scales: {
        x: {ticks: {maxRotation: 45, minRotation: 45, autoSkip: true, maxTicksLimit: 10}},
        ...axes,
    },
});

const series = (key, convert = v => v) => history.value.map(r => {
    const v = r[key];
    return v === null || v === undefined ? null : convert(v);
});

// Draw points when a series has only one or two readings, otherwise a lone value is invisible
const dataset = (label, data, color, yAxisID = 'y') => ({
    label, data, yAxisID, borderColor: color, backgroundColor: color, spanGaps: true, borderWidth: 2,
    pointRadius: data.filter(v => v !== null).length <= 2 ? 3 : 0, pointHitRadius: 6,
});

// Two flat lines filled between them shade the normal range without a plugin
const bandDatasets = (low, high, length, color) => {
    const flat = {band: true, borderWidth: 0, pointRadius: 0, pointHitRadius: 0, yAxisID: 'y'};
    return [
        {...flat, label: 'Normal range low', data: Array(length).fill(low), fill: false},
        {...flat, label: 'Normal range', data: Array(length).fill(high), fill: '-1', backgroundColor: color},
    ];
};

const summarize = (title, datasets) => {
    const parts = datasets.filter(d => !d.band).map((d) => {
        const values = d.data.filter(v => v !== null);
        if (!values.length) return null;
        return `${d.label} latest ${formatNumber(values[values.length - 1])}, low ${formatNumber(Math.min(...values))}, high ${formatNumber(Math.max(...values))}`;
    }).filter(Boolean);
    return `${title} chart over ${plural(history.value.length, 'reading')}. ${parts.join('; ')}.`;
};

const charts = computed(() => {
    const formatLabel = companyDateTimeFormatter({year: undefined, second: undefined});
    const labels = history.value.map(r => formatLabel(r.event_datetime));
    const units = unitLabels();
    const coolant = series('coolant_c', v => unitifyTemperature(v, false));
    const oil = series('oil_kpa', v => unitifyPressure(v, false));
    const battery = series('battery_v', v => Number(v));
    const def = series('def_pct', v => Number(v));
    const hours = series('engine_hours', v => Number(v));
    const any = (...lists) => lists.some(list => list.some(v => v !== null));
    const scale = batteryScale.value;
    const bandLow = BATTERY.bandLow * scale;
    const bandHigh = BATTERY.bandHigh * scale;
    const list = [
        {
            key: 'coolant-oil', title: 'Coolant and Oil Pressure', hasData: any(coolant, oil),
            datasets: [
                dataset(`Coolant (${units.temperature})`, coolant, '#E53935'),
                dataset(`Oil pressure (${units.pressure})`, oil, '#1E88E5', 'y1'),
            ],
            options: lineOptions({
                y: {position: 'left', title: {display: true, text: units.temperature}},
                y1: {position: 'right', grid: {drawOnChartArea: false}, title: {display: true, text: units.pressure}},
            }),
        },
        {
            key: 'battery', title: 'Battery Voltage', hasData: any(battery),
            datasets: [
                ...bandDatasets(bandLow, bandHigh, labels.length, 'rgba(0, 150, 136, 0.12)'),
                dataset('Battery (V)', battery, '#009688'),
            ],
            note: `Shaded band: normal range ${bandLow.toFixed(1)} to ${bandHigh.toFixed(1)} V`,
            options: lineOptions({y: {title: {display: true, text: 'V'}}}),
        },
        {
            key: 'def', title: 'DEF Level', hasData: any(def),
            datasets: [dataset('DEF (%)', def, '#7CB342')],
            options: lineOptions({y: {min: 0, max: 100, title: {display: true, text: '%'}}}),
        },
        {
            key: 'hours', title: 'Engine Hours (device reading)', hasData: any(hours),
            datasets: [dataset('Engine hours', hours, '#8E24AA')],
            options: lineOptions({y: {title: {display: true, text: 'h'}}}),
        },
    ];
    return list.map(chart => ({
        ...chart,
        data: {labels, datasets: chart.datasets},
        summary: summarize(chart.title, chart.datasets),
    }));
});

const fetchHealth = () => {
    loading.value = true;
    return send('get', `assets/${props.assetId}/vehicle-health`)
        .then((response) => {
            health.value = response.data.data ?? null;
            loadError.value = false;
            noAccess.value = false;
        })
        .catch((error) => {
            noAccess.value = isNoAccess(error);
            loadError.value = !noAccess.value;
            if (noAccess.value) emit('no-access');
            if (loadError.value && health.value) {
                showSnackbar(error.response?.data?.message ?? 'Vehicle health could not be loaded', 'error');
            }
        })
        .finally(() => loading.value = false);
};

// A range that fails the checks clears the charts so only the error shows
const rejectRange = (message) => {
    historyRequest++;
    historyLoading.value = false;
    history.value = [];
    historyTruncated.value = false;
    historyDays.value = null;
    rangeError.value = message;
};

const fetchHistory = () => {
    rangeError.value = '';
    const fromDay = dayNumber(fromDate.value);
    const toDay = dayNumber(toDate.value);
    if (fromDay === null || toDay === null) return rejectRange('Pick a start and end date.');
    if (toDay < fromDay) return rejectRange('The end date must be after the start date.');
    if (toDay - fromDay + 1 > MAX_RANGE_DAYS) return rejectRange(`Pick a range of ${MAX_RANGE_DAYS} days or less.`);
    const from = zonedInputToUtcIso(`${dayInput(fromDay)}T00:00`, tz);
    // The To day ends one second before the next local midnight
    const nextStart = zonedInputToUtcIso(`${dayInput(toDay + 1)}T00:00`, tz);
    if (!from || !nextStart) return rejectRange('Pick a start and end date.');
    const to = new Date(new Date(nextStart).getTime() - 1000).toISOString();

    const request = ++historyRequest;
    historyLoading.value = true;
    send('get', `assets/${props.assetId}/obd-params`, {from, to})
        .then((response) => {
            if (request !== historyRequest) return;
            const data = response.data.data;
            history.value = Array.isArray(data) ? data : (data?.rows ?? []);
            historyDays.value = toDay - fromDay + 1;
            // The header is only readable once CORS exposes it, so a full page also counts as cut off
            historyTruncated.value = String(response.headers?.['x-truncated'] ?? '') === '1'
                || history.value.length >= MAX_HISTORY_ROWS;
        })
        .catch((error) => {
            if (request !== historyRequest) return;
            history.value = [];
            historyTruncated.value = false;
            historyDays.value = null;
            // The page error already covers a failed health load
            if (!isNoAccess(error) && !(loadError.value && !health.value)) {
                showSnackbar(error.response?.data?.message ?? 'Engine history could not be loaded', 'error');
            }
        })
        .finally(() => {
            if (request === historyRequest) historyLoading.value = false;
        });
};

const onPresetChange = (value) => {
    if (value === 'custom') return;
    toDate.value = todayInput();
    fromDate.value = dayInput(dayNumber(toDate.value) - Number(value) + 1);
    fetchHistory();
};
// Custom dates apply as soon as they change
watch([fromDate, toDate], () => {
    if (rangePreset.value === 'custom') fetchHistory();
});

// History waits for the card so a failed card load shows one error, not two
const refresh = () => fetchHealth().then(fetchHistory);

onMounted(refresh);
</script>

<style scoped>
.chart-box {
    height: 260px;
    position: relative;
}
</style>
